import React, { useState } from 'react';
import { Plus, TrendingUp, Calendar, DollarSign, BarChart3, PieChart, Download } from 'lucide-react';
import { Dividend, DividendPortfolio } from '../types';
import { formatCurrency, calculateDividendYield } from '../utils/helpers';
import { mockDividends, mockDividendPortfolio } from '../utils/mockData';
import { DividendChart } from '../components/DividendChart';

interface DividendFormData {
  stock_symbol: string;
  stock_name: string;
  amount_per_share: string;
  shares_owned: string;
  ex_dividend_date: string;
  payment_date: string;
  frequency: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
}

export const DividendPage: React.FC = () => {
  const [dividends, setDividends] = useState<Dividend[]>(mockDividends);
  const [portfolio] = useState<DividendPortfolio>(mockDividendPortfolio);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'month' | 'quarter' | 'year'>('year');
  const [formData, setFormData] = useState<DividendFormData>({
    stock_symbol: '',
    stock_name: '',
    amount_per_share: '',
    shares_owned: '',
    ex_dividend_date: '',
    payment_date: '',
    frequency: 'quarterly'
  });

  const handleInputChange = (field: keyof DividendFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newDividend: Dividend = {
      id: Date.now().toString(),
      user_id: 'user1',
      stock_symbol: formData.stock_symbol.toUpperCase(),
      stock_name: formData.stock_name,
      amount_per_share: parseFloat(formData.amount_per_share) || 0,
      shares_owned: parseInt(formData.shares_owned) || 0,
      total_amount: (parseFloat(formData.amount_per_share) || 0) * (parseInt(formData.shares_owned) || 0),
      ex_dividend_date: formData.ex_dividend_date,
      payment_date: formData.payment_date,
      frequency: formData.frequency,
      currency: 'USD',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    setDividends([newDividend, ...dividends]);
    
    // Reset form
    setFormData({
      stock_symbol: '',
      stock_name: '',
      amount_per_share: '',
      shares_owned: '',
      ex_dividend_date: '',
      payment_date: '',
      frequency: 'quarterly'
    });
    
    setShowAddForm(false);
  };

  const resetForm = () => {
    setFormData({
      stock_symbol: '',
      stock_name: '',
      amount_per_share: '',
      shares_owned: '',
      ex_dividend_date: '',
      payment_date: '',
      frequency: 'quarterly'
    });
    setShowAddForm(false);
  };

  const totalAnnualDividendIncome = dividends.reduce((sum, dividend) => {
    const frequencyMultiplier = {
      monthly: 12,
      quarterly: 4,
      semi_annual: 2,
      annual: 1
    };
    return sum + (dividend.total_amount * frequencyMultiplier[dividend.frequency]);
  }, 0);

  const averageDividendYield = dividends.length > 0 
    ? dividends.reduce((sum, dividend) => sum + calculateDividendYield(dividend.total_amount, portfolio.total_value), 0) / dividends.length
    : 0;

  const upcomingDividends = dividends
    .filter(dividend => new Date(dividend.payment_date) > new Date())
    .sort((a, b) => new Date(a.payment_date).getTime() - new Date(b.payment_date).getTime())
    .slice(0, 5);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100">Dividend Portfolio</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track your dividend investments and income</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Dividend Stock
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Portfolio Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Portfolio Value</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(portfolio.total_value)}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm text-green-600 dark:text-green-400 font-medium">+12.4%</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Annual Dividend Income</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(totalAnnualDividendIncome)}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">Above Avg</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Dividend Yield</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{(averageDividendYield * 100).toFixed(2)}%</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Calendar className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">{dividends.length}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Dividend Stocks</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dividends.length}</p>
        </div>
      </div>

      {/* Dividend Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Dividend Income Trend</h2>
          <div className="flex gap-2">
            {(['month', 'quarter', 'year'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  selectedPeriod === period
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64">
          <DividendChart data={dividends} period={selectedPeriod} />
        </div>
      </div>

      {/* Dividend Stocks Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Dividend Stocks</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Shares</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Dividend/Share</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Next Payment</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {dividends.map((dividend) => (
                <tr key={dividend.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{dividend.stock_symbol}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{dividend.stock_name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {dividend.shares_owned.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {formatCurrency(dividend.amount_per_share)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {formatCurrency(dividend.total_amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {dividend.frequency.charAt(0).toUpperCase() + dividend.frequency.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {new Date(dividend.payment_date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upcoming Dividends */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Upcoming Dividend Payments</h2>
        <div className="space-y-3">
          {upcomingDividends.map((dividend) => (
            <div key={dividend.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">{dividend.stock_symbol}</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{dividend.stock_name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Payment Date: {new Date(dividend.payment_date).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(dividend.total_amount)}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{dividend.shares_owned} shares</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Dividend Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Add Dividend Stock</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stock Symbol</label>
                <input
                  type="text"
                  value={formData.stock_symbol}
                  onChange={(e) => handleInputChange('stock_symbol', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  placeholder="AAPL"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stock Name</label>
                <input
                  type="text"
                  value={formData.stock_name}
                  onChange={(e) => handleInputChange('stock_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Apple Inc."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dividend/Share</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount_per_share}
                    onChange={(e) => handleInputChange('amount_per_share', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    placeholder="0.24"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Shares Owned</label>
                  <input
                    type="number"
                    value={formData.shares_owned}
                    onChange={(e) => handleInputChange('shares_owned', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    placeholder="100"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ex-Dividend Date</label>
                  <input
                    type="date"
                    value={formData.ex_dividend_date}
                    onChange={(e) => handleInputChange('ex_dividend_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => handleInputChange('payment_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frequency</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => handleInputChange('frequency', e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Stock
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}; 